import { useEffect, useState } from "react"
import { Box, Button, HStack, SimpleGrid, Spinner, Stack } from "@chakra-ui/react"
import { useQueryClient } from "@tanstack/react-query"
import { Input, Text, toaster } from "@/components"
import { updateFundAccounting } from "@/api/irr"
import { useFundCapitalCalls, useFundDistributions } from "@/hooks/irr"
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { IrrFundDashboard } from "@/types"

const fmtMoney = (v: number) => {
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}K`
  return `$${v.toFixed(0)}`
}
const fmtMoneyFull = (v: number) =>
  `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const fmtPct = (v: number | null) =>
  v == null ? "—" : `${(v * 100).toFixed(1)}%`
const fmtX = (v: number | null) => (v == null ? "—" : `${v.toFixed(2)}×`)

const MetricCard = ({
  label,
  value,
  sub,
}: {
  label: string
  value: string
  sub?: string
}) => (
  <Box
    bg="card.bg"
    border="1px solid"
    borderColor="border.default"
    borderRadius="md"
    px={5}
    py={4}
  >
    <Text
      fontSize="10px"
      fontWeight="semibold"
      color="text.muted"
      textTransform="uppercase"
      letterSpacing="0.08em"
      mb={1}
    >
      {label}
    </Text>
    <Text fontSize="2xl" fontWeight="semibold" lineHeight="1.2" letterSpacing="-0.01em">
      {value}
    </Text>
    {sub && (
      <Text fontSize="xs" color="text.muted" mt={1}>
        {sub}
      </Text>
    )}
  </Box>
)

// Manual-entry accounting fields (always editable)
type ManualFieldKey = "cashAvailable" | "prepaidExpenses" | "prepaidLpCommitments" | "undistributedRealizations" | "lastDistributionDate" | "outstandingPayables"
interface ManualFieldDef {
  key: ManualFieldKey
  label: string
  type: "number" | "date"
}

const MANUAL_FIELDS: ManualFieldDef[] = [
  { key: "cashAvailable", label: "Cash Available", type: "number" },
  { key: "prepaidExpenses", label: "Prepaid Expenses", type: "number" },
  { key: "prepaidLpCommitments", label: "Prepaid LP Commitments", type: "number" },
  { key: "undistributedRealizations", label: "Undistributed Realizations", type: "number" },
  { key: "lastDistributionDate", label: "Last Distribution Date", type: "date" },
  { key: "outstandingPayables", label: "Outstanding Payables", type: "number" },
]

interface Props {
  funds: IrrFundDashboard[]
  isLoading: boolean
}

export const IrrDashboardTab = ({ funds, isLoading }: Props) => {
  const queryClient = useQueryClient()
  const [selectedFundId, setSelectedFundId] = useState<number>(funds[0]?.fundId ?? 1)
  const fund = funds.find((f) => f.fundId === selectedFundId) ?? funds[0]

  const [showAccounting, setShowAccounting] = useState(false)
  const [showCashFlows, setShowCashFlows] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [accountingForm, setAccountingForm] = useState<Record<string, string>>({})

  const { data: capitalCalls = [] } = useFundCapitalCalls(showCashFlows ? selectedFundId : null)
  const { data: distributions = [] } = useFundDistributions(showCashFlows ? selectedFundId : null)

  // Sync form state when fund changes or accounting panel opens
  useEffect(() => {
    if (fund && showAccounting) {
      const form: Record<string, string> = {}
      for (const f of MANUAL_FIELDS) {
        const val = fund[f.key as keyof IrrFundDashboard]
        form[f.key] = val != null ? String(val) : ""
      }
      setAccountingForm(form)
    }
  }, [fund?.fundId, showAccounting]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveAccounting = async () => {
    if (!fund) return
    setIsSaving(true)
    try {
      const fields: Record<string, number | string | null> = {}
      for (const f of MANUAL_FIELDS) {
        const val = accountingForm[f.key]
        if (f.type === "date") {
          fields[f.key] = val || null
        } else {
          fields[f.key] = val !== "" && val !== undefined ? Number(val) : null
        }
      }
      await updateFundAccounting(fund.fundId, fields)
      queryClient.invalidateQueries({ queryKey: ["irrDashboard"] })
      toaster.create({ title: "Fund accounting updated", type: "success" })
      setShowAccounting(false)
    } catch {
      toaster.create({ title: "Failed to update fund accounting", type: "error" })
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" py={12}>
        <Spinner size="lg" />
      </Box>
    )
  }

  if (!fund) {
    return <Text color="text.muted">No fund data available.</Text>
  }

  const hasAccounting = fund.totalPaidIn != null
  const chartData = fund.cashFlowsByPeriod.map((p) => ({
    period: p.period.replace("-", " "),
    investments: p.investments,
    realizations: p.realizations,
    cumulative: p.cumulative,
  }))

  // Merge capital calls and distributions into a single sorted list for display
  const allCashFlows = [
    ...capitalCalls.map((c) => ({ date: c.callDate, type: "Capital Call" as const, amount: c.amount, memo: c.memo })),
    ...distributions.map((d) => ({ date: d.distributionDate, type: "Distribution" as const, amount: d.amount, memo: d.memo })),
  ].sort((a, b) => a.date.localeCompare(b.date))

  return (
    <Stack gap={6}>
      {/* Fund selector */}
      <HStack gap={2} flexWrap="wrap" alignItems="center">
        {funds.map((f) => (
          <Button
            key={f.fundId}
            size="sm"
            variant={selectedFundId === f.fundId ? "solid" : "outline"}
            onClick={() => setSelectedFundId(f.fundId)}
          >
            {f.fundName}
          </Button>
        ))}
        <Text fontSize="sm" color="text.muted" ml={2}>
          {fund.dealCount} deal{fund.dealCount !== 1 ? "s" : ""} ·{" "}
          {fund.companyCount} compan{fund.companyCount !== 1 ? "ies" : "y"}
        </Text>
      </HStack>

      {/* KPI cards — 2 rows of 3 */}
      <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
        <MetricCard label="Total Invested" value={fmtMoney(fund.totalInvested)} />
        <MetricCard label="Residual @LRP" value={fmtMoney(fund.totalResidualAtLrp)} />
        <MetricCard label="Residual @2×LRP" value={fmtMoney(fund.totalResidualAt2xLrp)} />
        <MetricCard label="Total Realized" value={fmtMoney(fund.totalRealized)} />
        <MetricCard
          label="Total Value"
          value={fmtMoney(fund.totalValue)}
          sub={hasAccounting ? `Gross value @LRP (deal-level holding: ${fmtMoney(fund.totalHoldingValue)})` : `Holding value (FMV) ${fmtMoney(fund.totalHoldingValue)}`}
        />
        <MetricCard
          label="TVPI / DPI"
          value={`${fmtX(fund.tvpi)} / ${fmtX(fund.dpi)}`}
          sub={`IRR ${fmtPct(fund.fundIrr)}${hasAccounting ? " · LP-level" : ""}`}
        />
      </SimpleGrid>

      {/* Fund accounting section */}
      <Box>
        {!showAccounting ? (
          <Stack gap={2}>
            <HStack gap={3} alignItems="center">
              <Button size="xs" variant="outline" onClick={() => setShowAccounting(true)}>
                {hasAccounting ? "Edit" : "Set"} Fund Accounting
              </Button>
              {hasAccounting && (
                <Text fontSize="xs" color="text.muted">
                  Paid In: {fmtMoney(fund.totalPaidIn!)}{fund.hasFundCashFlows ? " (auto)" : ""} · Distributions: {fmtMoney(fund.totalDistributions ?? 0)}{fund.hasFundCashFlows ? " (auto)" : ""} · Cash: {fmtMoney(fund.cashAvailable ?? 0)}
                  {fund.realizedGpCarry ? ` · GP Carry: ${fmtMoney(fund.realizedGpCarry)}` : ""}
                  {fund.undistributedRealizations ? ` · Undistributed: ${fmtMoney(fund.undistributedRealizations)}` : ""}
                </Text>
              )}
            </HStack>
          </Stack>
        ) : (
          <Box p={4} border="1px solid" borderColor="gray.200" borderRadius="md" bg="bg.subtle">
            <Text fontSize="sm" fontWeight="bold" mb={3}>Fund Accounting</Text>

            {/* Auto-computed fields (read-only when cash flows exist) */}
            {fund.hasFundCashFlows && (
              <Box mb={4}>
                <Text fontSize="xs" fontWeight="semibold" color="text.muted" textTransform="uppercase" letterSpacing="0.08em" mb={2}>
                  Auto-computed from Cash Flows
                </Text>
                <SimpleGrid columns={{ base: 1, md: 3 }} gap={3}>
                  <Stack gap={0}>
                    <Text fontSize="xs" color="text.secondary">Total Paid In</Text>
                    <Text fontSize="sm" fontWeight="medium">{fund.totalPaidIn != null ? fmtMoneyFull(fund.totalPaidIn) : "—"}</Text>
                  </Stack>
                  <Stack gap={0}>
                    <Text fontSize="xs" color="text.secondary">Total Distributions</Text>
                    <Text fontSize="sm" fontWeight="medium">{fund.totalDistributions != null ? fmtMoneyFull(fund.totalDistributions) : "—"}</Text>
                  </Stack>
                  <Stack gap={0}>
                    <Text fontSize="xs" color="text.secondary">Realized GP Carry (20%)</Text>
                    <Text fontSize="sm" fontWeight="medium">{fund.realizedGpCarry != null ? fmtMoneyFull(fund.realizedGpCarry) : "—"}</Text>
                  </Stack>
                </SimpleGrid>
              </Box>
            )}

            {/* Manual entry fields */}
            <Text fontSize="xs" fontWeight="semibold" color="text.muted" textTransform="uppercase" letterSpacing="0.08em" mb={2}>
              {fund.hasFundCashFlows ? "Manual Entry" : "Fund Accounting Fields"}
            </Text>
            <SimpleGrid columns={{ base: 1, md: 3, lg: 5 }} gap={3} mb={3}>
              {/* If no cash flows, also show totalPaidIn/totalDistributions as editable */}
              {!fund.hasFundCashFlows && (
                <>
                  <Stack gap={1}>
                    <Text fontSize="xs" color="text.secondary">Total Paid In</Text>
                    <Input
                      type="number"
                      size="sm"
                      step="0.01"
                      placeholder="0.00"
                      value={accountingForm["totalPaidIn"] ?? ""}
                      onChange={(e) => setAccountingForm((prev) => ({ ...prev, totalPaidIn: e.target.value }))}
                    />
                  </Stack>
                  <Stack gap={1}>
                    <Text fontSize="xs" color="text.secondary">Total Distributions</Text>
                    <Input
                      type="number"
                      size="sm"
                      step="0.01"
                      placeholder="0.00"
                      value={accountingForm["totalDistributions"] ?? ""}
                      onChange={(e) => setAccountingForm((prev) => ({ ...prev, totalDistributions: e.target.value }))}
                    />
                  </Stack>
                </>
              )}
              {MANUAL_FIELDS.map((f) => (
                <Stack key={f.key} gap={1}>
                  <Text fontSize="xs" color="text.secondary">{f.label}</Text>
                  <Input
                    type={f.type === "date" ? "text" : "number"}
                    size="sm"
                    step={f.type === "number" ? "0.01" : undefined}
                    placeholder={f.type === "date" ? "YYYY-MM-DD" : "0.00"}
                    value={accountingForm[f.key] ?? ""}
                    onChange={(e) => setAccountingForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                  />
                </Stack>
              ))}
            </SimpleGrid>
            <HStack gap={2}>
              <Button size="sm" onClick={handleSaveAccounting} loading={isSaving}>
                Save
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowAccounting(false)}>
                Cancel
              </Button>
            </HStack>
          </Box>
        )}
      </Box>

      {/* LP Cash Flow History (collapsible) */}
      {fund.hasFundCashFlows && (
        <Box>
          <Button
            size="xs"
            variant="outline"
            onClick={() => setShowCashFlows(!showCashFlows)}
            mb={showCashFlows ? 3 : 0}
          >
            {showCashFlows ? "Hide" : "Show"} LP Cash Flow History ({capitalCalls.length + distributions.length} entries)
          </Button>

          {showCashFlows && allCashFlows.length > 0 && (
            <Box
              border="1px solid"
              borderColor="border.default"
              borderRadius="md"
              overflow="hidden"
            >
              <Box overflowX="auto">
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid var(--chakra-colors-border-default)" }}>
                      <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 600 }}>Date</th>
                      <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 600 }}>Type</th>
                      <th style={{ textAlign: "right", padding: "8px 12px", fontWeight: 600 }}>Amount</th>
                      <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 600 }}>Memo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allCashFlows.map((cf, i) => (
                      <tr
                        key={`${cf.date}-${cf.type}-${cf.amount}-${i}`}
                        style={{ borderBottom: "1px solid var(--chakra-colors-border-default)" }}
                      >
                        <td style={{ padding: "6px 12px" }}>{cf.date}</td>
                        <td style={{ padding: "6px 12px" }}>
                          <Text
                            as="span"
                            fontSize="xs"
                            px={2}
                            py={0.5}
                            borderRadius="sm"
                            bg={cf.type === "Capital Call" ? "red.50" : "green.50"}
                            color={cf.type === "Capital Call" ? "red.700" : "green.700"}
                            fontWeight="medium"
                          >
                            {cf.type}
                          </Text>
                        </td>
                        <td style={{ padding: "6px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                          {fmtMoneyFull(cf.amount)}
                        </td>
                        <td style={{ padding: "6px 12px", color: "var(--chakra-colors-text-muted)" }}>
                          {cf.memo || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: "2px solid var(--chakra-colors-border-default)", fontWeight: 600 }}>
                      <td style={{ padding: "8px 12px" }}>Total</td>
                      <td style={{ padding: "8px 12px" }}>
                        {capitalCalls.length} calls / {distributions.length} distributions
                      </td>
                      <td style={{ padding: "8px 12px", textAlign: "right" }}>
                        <Text as="span" color="red.600">-{fmtMoneyFull(capitalCalls.reduce((s, c) => s + c.amount, 0))}</Text>
                        {" / "}
                        <Text as="span" color="green.600">+{fmtMoneyFull(distributions.reduce((s, d) => s + d.amount, 0))}</Text>
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Cash flow chart */}
      {chartData.length > 0 && (
        <Box
          bg="card.bg"
          border="1px solid"
          borderColor="border.default"
          borderRadius="md"
          px={5}
          pt={5}
          pb={4}
        >
          <Text fontSize="sm" fontWeight="medium" mb={4}>
            Cash Flows by Quarter
          </Text>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart
              data={chartData}
              margin={{ top: 4, right: 8, bottom: 0, left: 8 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#e2e8f0"
                vertical={false}
              />
              <XAxis
                dataKey="period"
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                tickFormatter={(v) => fmtMoney(v as number)}
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                width={56}
              />
              <Tooltip
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                formatter={((value: number, name: string) => {
                  const label =
                    name === "investments"
                      ? "Investments"
                      : name === "realizations"
                      ? "Realizations"
                      : "Cumulative"
                  return [
                    `$${Math.abs(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
                    label,
                  ]
                }) as any}
              />
              <Bar dataKey="investments" fill="#3182ce" radius={[2, 2, 0, 0]} />
              <Bar dataKey="realizations" fill="#38a169" radius={[2, 2, 0, 0]} />
              <Line
                type="monotone"
                dataKey="cumulative"
                stroke="#805ad5"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
          <HStack gap={4} mt={3} justifyContent="center">
            {[
              { color: "#3182ce", label: "Investments" },
              { color: "#38a169", label: "Realizations" },
              { color: "#805ad5", label: "Cumulative net" },
            ].map(({ color, label }) => (
              <HStack key={label} gap={1.5}>
                <Box w="10px" h="10px" borderRadius="sm" bg={color} />
                <Text fontSize="xs" color="text.muted">
                  {label}
                </Text>
              </HStack>
            ))}
          </HStack>
        </Box>
      )}
    </Stack>
  )
}
